import React, { useEffect, useState } from 'react'
import { Outlet, useNavigate, useLocation, NavLink } from 'react-router-dom'
import { useAdminAuth } from '../contexts/AdminAuthContext'
import { 
  Users, 
  BarChart3, 
  Settings, 
  LogOut, 
  Menu, 
  X, 
  Home,
  Shield,
  Building,
  User,
  CreditCard,
  MessageSquare,
  Send
} from 'lucide-react'
import { supabaseAdmin } from '../lib/supabaseClient'

const AdminLayout = () => {
  const { user, signOut } = useAdminAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const handleSignOut = async () => {
    await signOut()
    navigate('login')
  }

  const [sidebarOpen, setSidebarOpen] = useState(false)

  // Persistir usuário admin no cache com a validade do token
  useEffect(() => {
    const persist = async () => {
      if (user?.admin) {
        try {
          const { data: { session } } = await supabaseAdmin.auth.getSession()
          const expMs = session?.expires_at ? session.expires_at * 1000 : undefined
          localStorage.setItem('aguiar_admin_user', JSON.stringify(user))
          if (expMs) localStorage.setItem('aguiar_admin_exp', String(expMs))
        } catch {}
      }
    }
    persist()
  }, [user])

  // Use rotas relativas ao /admin-panel via BrowserRouter basename.
  const navigation = [
    { name: 'Dashboard', to: '.', icon: Home },
    { name: 'Usuários', to: 'users', icon: Users },
    { name: 'Planos', to: 'plans', icon: CreditCard },
    { name: 'Chamados', to: 'tickets', icon: MessageSquare },
    { name: 'Mensagens', to: 'messages', icon: Send },
    { name: 'Analytics', to: 'analytics', icon: BarChart3 },
    { name: 'Configurações', to: 'settings', icon: Settings },
  ] as const

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div className="fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 flex-shrink-0"
           style={{ transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)' }}>
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Shield className="w-8 h-8 text-admin-600" />
            <h1 className="text-xl font-bold text-gray-900">Aguiar Gestor</h1>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="mt-6 px-3">
          <div className="space-y-1">
            {navigation.map((item) => (
              <NavLink
                end={item.to === '.'}
                key={item.name}
                to={item.to}
                className={({ isActive }) =>
                  `group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    isActive
                      ? 'bg-admin-100 text-admin-700 border-r-2 border-admin-600'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <item.icon className={`mr-3 h-5 w-5 ${isActive ? 'text-admin-600' : 'text-gray-400 group-hover:text-gray-500'}`} />
                    {item.name}
                  </>
                )}
              </NavLink>
            ))}
          </div>
        </nav>

        {/* User Info & Logout */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200">
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-8 h-8 bg-admin-100 rounded-full flex items-center justify-center">
              <User className="w-4 h-4 text-admin-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {user?.nome || user?.email}
              </p>
              <p className="text-xs text-gray-500 truncate">Administrador</p>
            </div>
          </div>
          <button
            onClick={handleSignOut}
            className="w-full flex items-center justify-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sair
          </button>
        </div>
      </div>

      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 bg-white rounded-md shadow-lg text-gray-600 hover:text-gray-900"
        >
          <Menu className="w-5 h-5" />
        </button>
      </div>

      {/* Main content */}
      <div className="lg:ml-64 flex-1 min-w-0">
        <div className="p-4 lg:p-8">
          <Outlet />
        </div>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  )
}

export default AdminLayout

